<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <PortalReloadDialog :reloadDialog="reloadDialog"/>
        <v-alert
                v-model="exportAlert"
                dismissible
                type="success">
            Export requested.
        </v-alert>
        <v-toolbar flat color="white">
            <v-toolbar-title>Patient IDs</v-toolbar-title>
            <v-spacer/>
            <v-text-field
                    v-model="searchComputed"
                    append-icon="mdi-magnify"
                    label="Search by Patient ID, Group ID, or Name"
                    single-line
                    hide-details
                    clearable
                    @click:clear="handleClearSearch"
                    v-debounce:300="handleSearch">
            </v-text-field>
            <v-spacer/>
            <PatientDataDialog :dialog="patientDataDialog" :newPatient="newPatient" @dialog-close="patientDataDialogClose"/>
            <PatientBatchDataDialog :dialog="patientBatchDataDialog" @dialog-close="patientBatchDataDialogClose"/>
            <MrnNotesDialog :dialog="mrnNotesDialog" @dialog-close="mrnNotesDialogClose"/>
            <TrendingDialog :dialog="trendingDialog" @dialog-close="trendingDialogClose"/>
            <v-dialog v-model="exportDateRangeDialog" max-width="540px">
                <v-card min-height="500">
                    <v-card-title>
                        <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">Create Date Range Export</span>
                    </v-card-title>
                    <v-card-text>
                        <v-progress-linear v-if="waitingOnServer" :indeterminate="true"/>
                        <v-container>
                            <v-row>
                                <v-col :cols="8">
                                    <v-menu
                                        v-model="exportStartDateMenu"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="240px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="exportStartDateFormatted"
                                                label="Start Date"
                                                readonly
                                                prepend-icon="mdi-calendar"
                                                v-on="on">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker color="blue" v-model="exportStartDate" no-title @input="exportStartDateMenu = false"/>
                                    </v-menu>
                                </v-col>
                                <v-col :cols="4">
                                    <v-text-field
                                        v-model="exportStartTime"
                                        label="Start Time"
                                        type="time">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col :cols="8">
                                    <v-menu
                                        v-model="exportEndDateMenu"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="240px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="exportEndDateFormatted"
                                                label="End Date"
                                                readonly
                                                prepend-icon="mdi-calendar"
                                                v-on="on">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker color="blue" v-model="exportEndDate" no-title @input="exportEndDateMenu = false"/>
                                    </v-menu>
                                </v-col>
                                <v-col :cols="4">
                                    <v-text-field
                                        v-model="exportEndTime"
                                        label="End Time"
                                        type="time">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer/>
                        <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
                        <v-btn color="blue darken-1" text @click="exportDataForDateRange">Export</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-toolbar>
        <v-toolbar flat color="white">
            <v-menu offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn class="ml-2" color="accent" v-on="on">
                        <span style="color: #000000;">New Patient(s)</span>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item
                        v-for="(item, index) in newPatientItems"
                        :key="index"
                        @click="newPatientAction(index)">
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-menu offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn class="ml-2" color="accent" v-on="on">
                        <span style="color: #000000;">Export Tests</span>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item
                            v-for="(item, index) in exportItems"
                            :key="index"
                            @click="exportTestsAction(index)">
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-icon large class="ml-6" @click="loadItems2">
                mdi-refresh
            </v-icon>
            <v-spacer/>
            <v-checkbox
                class="mt-5"
                type="checkbox"
                true-value="true"
                label="See Organization Tests"
                v-model="seeOrganizationTestsComputed"
                color="primary"
                v-if="currentUser && currentUser.orgId"
                @change="seeOrganizationTestsToggle"/>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        v-model="selected"
                        :headers="headers"
                        :items="mrns"
                        item-key="id"
                        :must-sort=true
                        @click:row="rowSelected"
                        @current-items="currentItems"
                        @item-selected="rowChecked"
                        show-select
                        :single-expand=true
                        :expanded.sync="expanded"
                        :options.sync="paginationComputed"
                        :server-items-length="totalRecords"
                        :loading="waiting"
                        :footer-props="{'items-per-page-options':[15, 30, 50, 100]}"
                        class="elevation-1"
                        @update:options="loadItems">
                        <template v-slot:header.data-table-select="{ props }">
                            <v-checkbox
                                    v-bind="props"
                                    color="primary"
                                    @click.stop="toggleAll">
                            </v-checkbox>
                        </template>
                        <template v-slot:item.mrn="{ item }">
                            <v-badge
                                :value = "item.testsPendingViewCount"
                                :color="item.testsPendingViewCount ? 'red' : 'white'"
                                :content=item.testsPendingViewCount>
                                {{ item.mrn }}
                            </v-badge>
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn color="accent" v-on="on">
                                        <span style="color: #000000;">Actions</span>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="(actionItem, index) in actionItems"
                                        :key="index"
                                        @click="testAction(index, item)">
                                        <v-list-item-icon><v-icon color="black" v-text="actionItem.icon"></v-icon></v-list-item-icon>
                                        <v-list-item-content><v-list-item-title v-text="actionItem.title"></v-list-item-title></v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                        <template v-slot:item.expanded="{ item }">
                            <v-row justify-Left>
                                <v-icon large v-if="!expanded.includes(item)">mdi-chevron-right</v-icon>
                                <v-icon large v-else>mdi-chevron-down</v-icon>
                            </v-row>
                        </template>
                        <template v-slot:expanded-item="props">
                            <td :colspan=12>
                                <v-expand-transition>
                                    <v-card class="elevation-10">
                                        <v-card-text>
                                            <ListMrnTests :mrnPredicate="mrnPredicate" :showExportCheckboxes="true" @refreshMsg="refreshMsg" @testSelectedMsg="testSelected"/>
                                        </v-card-text>
                                    </v-card>
                                </v-expand-transition>
                            </td>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
        <ConfirmationDialog v-if="confirmDeleteMrn" :params="confirmDeleteMrnParams" @confirmation-dialog-response="confirmDeleteMrnHandler"></ConfirmationDialog>
        <ConfirmationDialog v-if="confirmNoSelection" :params="confirmNoSelectionParams" @confirmation-dialog-response="confirmNoSelectionHandler"></ConfirmationDialog>
        <ConfirmationDialog v-if="confirmNoDateRange" :params="confirmNoDateRangeParams" @confirmation-dialog-response="confirmNoDateRangeHandler"></ConfirmationDialog>
        <ConfirmationDialog v-if="alertExportEndDateBeforeStartDate" :params="alertExportEndDateBeforeStartDateParams" @confirmation-dialog-response="alertExportEndDateBeforeStartDateHandler"></ConfirmationDialog>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex'
    import ListMrnTests from '@/components/list-mrn-tests';
    import PortalReloadDialog from '@/components/portal-reload-dialog'
    import MrnNotesDialog from '@/components/mrn-notes-dialog'
    import TrendingDialog from '@/components/trending-dialog'
    import ConfirmationDialog from '@/components/confirmation-dialog'
    import PatientDataDialog from '@/components/patient-data-dialog'
    import PatientBatchDataDialog from '@/components/patient-batch-data-dialog'

    export default
    {
        name: 'patient-ids',
        components:
        {
            ListMrnTests,
            PortalReloadDialog,
            MrnNotesDialog,
            TrendingDialog,
            ConfirmationDialog,
            PatientDataDialog,
            PatientBatchDataDialog
        },
        data: function ()
        {
            return {
                mrnPredicate: {},
                mrn: {},
                expandOrHideRow: true,
                exportDataCriteria: {startDate: null, endDate: null, includeOrg: false, tests: []},
                exportDateRangeDialog: false,
                exportStartDateMenu: false,
                exportStartDateFormatted: '',
                exportStartDateTime: null,
                exportStartDate: null,
                exportStartTime: null,
                exportEndDateMenu: false,
                exportEndDateFormatted: '',
                exportEndDateTime: null,
                exportEndDate: null,
                exportEndTime: null,
                waitingOnServer: false,
                pagination: {},
                selected: [],
                expanded: [],
                lastItemsPerPage: 10,
                headers:
                [
                    {
                        text: 'Tests',
                        value: 'expanded',
                        width: '40px',
                        sortable: false
                    },
                    {
                        text: 'Actions',
                        value: 'actions',
                        width: '1px',
                        sortable: false
                    },
                    {
                        text: 'Patient ID',
                        value: 'mrn',
                        width: '240px',
                    },
                    {
                        text: 'Name',
                        value: 'name',
                        width: '240px'
                    },
                    {
                        text: 'Age',
                        value: 'age',
                        width: '40px',
                        sortable: false
                    },
                    {
                        text: 'Group ID',
                        value: 'groupId',
                        width: '240px',
                    },
                    {
                        text: 'Last Activity Date',
                        value: 'updatedAt',
                        width: '190px'
                    },
                    {
                        text: 'Last Test Administrator',
                        value: 'displayName',
                        width: '240px',
                        sortable: false
                    }
                ],
                exportAlert: false,
                newPatientItems:
                [
                    { title: 'Single Patient' },
                    { title: 'Batch - CSV File' }
                ],
                exportItems:
                [
                    { title: 'Selected' },
                    { title: 'By Date Range' }
                ],
                actionItems:
                [
                    { title: 'Trending', icon: 'mdi-chart-line' },
                    { title: 'Notes', icon: 'mdi-notebook-multiple' },
                    { title: 'Export', icon: 'mdi-email' },
                    { title: 'Edit', icon: 'mdi-pencil' },
                    { title: 'Delete', icon: 'mdi-delete' }
                ],
                reloadDialog: false,
                mrnNotesDialog: false,
                trendingDialog: false,
                patientDataDialog: false,
                newPatient: false,
                patientBatchDataDialog: false,
                confirmDeleteMrn: false,
                confirmDeleteMrnParams:
                {
                    title: 'Delete Patient ID',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                },
                confirmNoSelection: false,
                confirmNoSelectionParams:
                {
                    title: 'Export Warning',
                    text: 'Selecting no records will export all tests in your account. Are you sure you want to continue?',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No'
                },
                confirmNoDateRange: false,
                confirmNoDateRangeParams:
                {
                    title: 'Export Warning',
                    text: 'Selecting no date range will export all tests in your account. Are you sure you want to continue?',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No'
                },
                alertExportEndDateBeforeStartDate: false,
                alertExportEndDateBeforeStartDateParams:
                {
                    title: 'Export Error',
                    text: 'The selected ending date/time is before the selected starting date/time. Please correct.',
                    positiveResponse: 'OK',
                },
                testToken: 'xxx'
            }
        },
        async mounted()
        {
            this.testToken = this.$route.params.testToken;
            if (this.testToken)
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_MRN({testToken: this.testToken})
                    this.WAITING(false);
                    this.mrnPredicate = {mrn: this.mrns[0].mrn, testToken: this.testToken};
                    this.mrn = this.mrns[0];
                    this.expanded.push(this.mrns[0])
                }
                catch (error)
                {
                    this.WAITING(false);
                    this.testToken = null;
                    await this.loadItems();
                    // If the patient record associated with the test request was not found
                    if (404 === error.response.status)
                    {
                        this.SHOW_SNACKBAR_WARN_DELAY({message: 'The associated patient ID could not be found. Make sure you are logged in with the correct user account and that the patient or patient test record still exists.', delay: 10000})
                    }
                    else
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message)
                    }
                }
            }
            else
            {
                this.testToken = null;
                await this.loadItems()
            }
        },
        destroyed()
        {
            this.CLEAR_TESTS()
        },
        watch:
        {
            exportDateRangeDialog(val)
            {
                // If the export by date/time dialog is being opened, set defaults
                if (val)
                {
                    const currentTime = new Date().toISOString().split('T')[0];
                    this.exportStartDate = currentTime;
                    this.exportStartDateFormatted = this.formatDate(this.exportStartDate);
                    this.exportStartTime = '00:00:00.000';
                    this.exportEndDate = currentTime;
                    this.exportEndDateFormatted = this.formatDate(this.exportStartDate);
                    this.exportEndTime = '00:00:00.000'
                }
            },
            exportStartDate(val)
            {
                this.exportStartDateFormatted = this.formatDate(val);
                this.exportStartDateTime = new Date(`${val}T${this.formatDateForTime(this.exportStartDateTime)}`)
            },
            exportStartTime(val)
            {
                if (!val)
                {
                    return
                }
                this.exportStartDateTime = new Date(`${this.formatDateForCalendar(this.exportStartDateTime)}T${val}`)
            },
            exportEndDate(val)
            {
                this.exportEndDateFormatted = this.formatDate(val);
                this.exportEndDateTime = new Date(`${val}T${this.formatDateForTime(this.exportEndDateTime)}`)
            },
            exportEndTime(val)
            {
                if (!val)
                {
                    return
                }
                this.exportEndDateTime = new Date(`${this.formatDateForCalendar(this.exportEndDateTime)}T${val}`)
            }
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser,
                seeOrganizationTests: state => state.seeOrganizationTests,
                searchValue: state => state.searchValue
            }),
            ...mapState('mrns',
            {
                mrns: state => state.mrns,
                totalRecords: state => state.totalRecords,
                latestPortalVer: state => state.latestPortalVer
            }),
            ...mapState('tests',
            {
                patientData: state => state.patientData,
                tests: state => state.tests
            }),
            ...mapState('exportResult',
            {
                mrnSelections: state => state.mrnSelections,
                testSelections: state => state.testSelections
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            seeOrganizationTestsComputed:
            {
                get: function ()
                {
                    return this.seeOrganizationTests
                },
                set: function (newValue)
                {
                    this.SET_SEE_ORGANIZATION_TESTS(newValue);
                    this.currentUser.preferences.seeOrganizationTests = newValue;
                    this.UPDATE_CURRENT_USER_NO_WAIT(this.currentUser)
                }
            },
            searchComputed:
            {
                get: function ()
                {
                    return this.searchValue
                },
                set: function (newValue)
                {
                    this.pagination.page = 1;
                    this.SET_SEARCH_VALUE(newValue)
                }
            },
            paginationComputed:
            {
                get: function ()
                {
                    if (!this.pagination.sortBy || 0 === this.pagination.sortBy.length)
                    {
                        if (!this.pagination.sortBy)
                        {
                            this.pagination =
                            {
                                sortBy: [],
                                sortDesc: []
                            }
                        }
                        // Use the user's saved preferences if they have them
                        if (this.currentUser.preferences.patientListSort)
                        {
                            this.pagination.sortBy.push(this.currentUser.preferences.patientListSort.sortValue);
                            this.pagination.sortDesc.push(-1 === this.currentUser.preferences.patientListSort.sortOrder)
                        }
                        else // Else just use a default
                        {
                            this.pagination.sortBy.push('updatedAt');
                            this.pagination.sortDesc.push(true)
                        }
                    }
                    return this.pagination
                },
                set: function (newValue)
                {
                    this.pagination = newValue;
                    if (!this.currentUser.preferences.patientListSort)
                    {
                        this.currentUser.preferences.patientListSort =
                        {
                            sortValue: newValue.sortBy.length > 0 ? newValue.sortBy[0] : 'updatedAt',
                            sortOrder: newValue.sortDesc.length > 0 ? newValue.sortDesc[0] ? -1 : 1 : -1
                        }
                    }
                    else
                    {
                        if (newValue.sortBy[0] === this.currentUser.preferences.patientListSort.sortValue && (newValue.sortDesc[0] ? -1 : 1) === this.currentUser.preferences.patientListSort.sortOrder)
                        {
                            return
                        }
                        this.currentUser.preferences.patientListSort.sortValue = newValue.sortBy[0];
                        this.currentUser.preferences.patientListSort.sortOrder = newValue.sortDesc[0] ? -1 : 1
                    }
                    this.UPDATE_CURRENT_USER_NO_WAIT(this.currentUser).catch()
                }
            }
        },
        methods:
        {
            ...mapActions('mrns', ['GET_MRNS_WITH_SORT', 'DELETE_MRN', 'GET_MRN']),
            ...mapMutations('tests', ['CLEAR_TESTS', 'INIT_PATIENT_DATA', 'UPDATE_MUTATED_PATIENT_DATA']),
            ...mapActions('tests', ['GET_TESTS', 'GET_TEST_FOR_TEST_TOKEN']),
            ...mapMutations('auth', ['SET_SEE_ORGANIZATION_TESTS', 'SET_SEARCH_VALUE']),
            ...mapActions('auth', ['UPDATE_CURRENT_USER', 'UPDATE_CURRENT_USER_NO_WAIT']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_SUCCESS', 'SHOW_SNACKBAR_WARN_DELAY', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            ...mapActions('exportResult', ['EXPORT_RESULT_DATA']),
            ...mapMutations('exportResult', ['ADD_MRN_SELECTION', 'ADD_MRN_SELECTION_ONLY', 'RESET_MRN_SELECTIONS', 'RESET_MRN_SELECTIONS_FOR_PAGE', 'ADD_TEST_SELECTION']),
            async handleSearch()
            {
                this.testToken = null;
                await this.loadItems()
            },
            async handleClearSearch()
            {
                this.testToken = null;
                this.searchComputed = '';
                await this.loadItems()
            },
            rowSelected(item)
            {
                // Do not expand or hide the row if an action in a row was selected
                if (!this.expandOrHideRow)
                {
                    this.expandOrHideRow = true
                }
                else
                {
                    this.expanded = this.expanded.length > 0 && this.expanded[0].id === item.id ? [] : [item]
                }
                this.mrnPredicate = {mrn: item.mrn};
                this.mrn = item
            },
            currentItems(items)
            {
                if (this.paginationComputed.page !== 1 && 0 === items.length)
                {
                    this.paginationComputed.page -= 1
                }
            },
            async loadItems()
            {
                // If we have a test token we just want to look at the one associated patient
                if (this.testToken)
                {
                    return
                }
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_MRNS_WITH_SORT({pagination: this.paginationComputed, searchValue: this.searchComputed, userOnly: !this.seeOrganizationTests});

                    if (process.env.VUE_APP_VERSION < this.latestPortalVer)
                    {
                        this.reloadDialog = true
                    }

                    // If the rows per page changes, reset the selected tests
                    if (this.lastItemsPerPage !== this.paginationComputed.itemsPerPage)
                    {
                        this.lastItemsPerPage = this.paginationComputed.itemsPerPage;
                        this.RESET_MRN_SELECTIONS()
                    }
                    this.selected = this.mrnSelections.filter((item => item.page === this.paginationComputed.page));
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            async loadItems2()
            {
                this.testToken = null;
                await this.loadItems()
            },
            newPatientData()
            {
                this.INIT_PATIENT_DATA();
                this.newPatient = true;
                this.patientDataDialog = true
            },
            async editPatientData(item)
            {
                if (await this.getTestsCommon(item.mrn))
                {
                    this.UPDATE_MUTATED_PATIENT_DATA(this.patientData);
                    this.newPatient = false;
                    this.patientDataDialog = true
                }
            },
            async patientDataDialogClose(payload)
            {
                this.patientDataDialog = false
                if (payload)
                {
                    await this.refreshMsg()
                }
            },
            patientBatchDataDialogClose(payload)
            {
                if (payload)
                {
                    this.SHOW_SNACKBAR_SUCCESS('File successfully sent for processing. Look for email confirmation.')
                }
                this.patientBatchDataDialog = false
            },
            deleteMrn(item)
            {
                this.expandOrHideRow = false;
                this.confirmDeleteMrnParams.text = `Are you sure you want to delete Patient ID: ${item.mrn}?`;
                this.confirmDeleteMrnParams.context = item;
                this.confirmDeleteMrn = true
            },
            async confirmDeleteMrnHandler(payload)
            {
                this.confirmDeleteMrn = false;
                // If the MRN should be deleted
                if (payload.response)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        this.mrnPredicate = {};
                        this.mrn = {};
                        // Collapse all the rows
                        this.expanded = [];
                        await this.DELETE_MRN({mrn: payload.context.mrn});
                        this.testToken = null;
                        await this.loadItems()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                    }
                    this.WAITING(false)
                }
            },
            async exportDataSingleItem(item)
            {
                this.shouldCollapse(item.mrn);
                this.HIDE_SNACKBAR();
                this.WAITING(true);
                try
                {
                    await this.EXPORT_RESULT_DATA(
                    {
                        includeOrg: this.seeOrganizationTests,
                        tests:
                        [
                            {
                                mrn: item.mrn
                            }
                        ]
                    });
                    this.exportAlert = true;
                    setTimeout(() =>
                    {
                        this.exportAlert = false;
                    }, 1500)
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            async refreshMsg()
            {
                this.testToken = null;
                await this.loadItems()
            },
            async toggleAll()
            {
                // Collapse all the rows
                this.expanded = [];

                const selectAllRows = this.selected.length === 0 || (this.selected.length !== this.mrns.length && this.selected.length !== this.totalRecords && this.selected.length !== this.paginationComputed.itemsPerPage);
                for (const mrn of this.selected)
                {
                    this.RESET_MRN_SELECTIONS_FOR_PAGE(mrn);
                }
                if (selectAllRows)
                {
                    for (const mrn of this.mrns)
                    {
                        await this.rowChecked({item: mrn})
                    }
                }
                this.selected = this.mrnSelections.filter((item => item.page === this.paginationComputed.page));
            },
            async rowChecked(item)
            {
                if (!this.mrnSelections.some((item2 => item2.mrn === item.item.mrn)))
                {
                    this.WAITING(true);
                    try
                    {
                        this.HIDE_SNACKBAR();
                        await this.GET_TESTS({mrn: item.item.mrn});
                        for (const test of this.tests)
                        {
                            this.ADD_TEST_SELECTION(test)
                        }
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                        this.WAITING(false);
                        return
                    }
                    this.WAITING(false)
                }
                item.item.page = this.paginationComputed.page;
                this.ADD_MRN_SELECTION(item.item);
            },
            async testAction(index, item)
            {
                switch (index)
                {
                    case 0: // Trending
                        await this.trending(item);
                        break
                    case 1: // Notes
                        await this.notesItem(item);
                        break
                    case 2: // Export
                        await this.exportDataSingleItem(item);
                        break
                    case 3: // Edit
                        await this.editPatientData(item);
                        break
                    default: // Delete
                        this.deleteMrn(item)
                }
            },
            async newPatientAction(item)
            {
                // If single paient creation
                if (item === 0)
                {
                    this.newPatientData()
                }
                else // Else batch CSV creation
                {
                    this.patientBatchDataDialog = true
                }
            },
            async exportTestsAction(item)
            {
                // If the user has not selected an export type, direct them to the settings page
                if (!('exportType' in this.currentUser.preferences) || 'UNKNOWN' === this.currentUser.preferences.exportType)
                {
                    this.SHOW_SNACKBAR_ERROR("No export type has been selected. Please go to Customize and choose an Export Type.");
                    try
                    {
                        await this.$router.replace(
                        {
                            name: 'Settings'
                        });
                    }
                    catch (error)
                    {
                        console.log(error)
                    }
                    return
                }

                // If selected export
                if (item === 0)
                {
                    if (this.testSelections.length === 0)
                    {
                        // If the user has PDF or ALL as their export preference, don't allow no selections
                        if ('PDF' === this.currentUser.preferences.exportType || 'ALL' === this.currentUser.preferences.exportType)
                        {
                            this.SHOW_SNACKBAR_ERROR("For PDF or ALL exports you must select at least one test.");
                            return
                        }
                        this.confirmNoSelection = true;
                        return
                    }
                    this.exportData()
                }
                else // Else selected date range export
                {
                    // If the user has PDF or ALL as their export preference, don't allow date range exports
                    if ('PDF' === this.currentUser.preferences.exportType || 'ALL' === this.currentUser.preferences.exportType)
                    {
                        this.SHOW_SNACKBAR_ERROR("PDF or ALL date range exports are not supported.")
                    }
                    else
                    {
                        this.exportDateRangeDialog = true
                    }
                }
            },
            async confirmNoSelectionHandler(payload)
            {
                this.confirmNoSelection = false;
                // If the data should be exported
                if (payload.response)
                {
                    this.exportData()
                }
            },
            close()
            {
                this.waitingOnServer = false;
                this.exportDateRangeDialog = false;
                this.exportDataCriteria = {startDate: null, endDate: null, includeOrg: false, tests: []};
            },
            exportDataForDateRange()
            {
                this.exportDataCriteria.startDate = this.exportStartDateTime.toISOString();
                this.exportDataCriteria.endDate = this.exportEndDateTime.toISOString();
                if (!this.exportDataCriteria.startDate && !this.exportDataCriteria.endDate)
                {
                    this.confirmNoDateRange = true;
                    return
                }
                if (this.exportDataCriteria.startDate && this.exportDataCriteria.endDate && this.exportDataCriteria.endDate < this.exportDataCriteria.startDate)
                {
                    this.alertExportEndDateBeforeStartDate = true;
                    return
                }
                this.exportData()
            },
            async confirmNoDateRangeHandler(payload)
            {
                this.confirmNoDateRange = false;
                // If the data should be exported
                if (payload.response)
                {
                    await this.exportData()
                }
            },
            async alertExportEndDateBeforeStartDateHandler(payload)
            {
                this.alertExportEndDateBeforeStartDate = false;
                this.exportDateRangeDialog = true
            },
            async exportData()
            {
                this.HIDE_SNACKBAR();
                this.waitingOnServer = true;
                try
                {
                    this.exportDataCriteria.includeOrg = this.seeOrganizationTests;
                    let prevTestSelection = null;
                    let index = 0;
                    for (let testSelection of this.testSelections)
                    {
                        if (testSelection.mrn !== prevTestSelection)
                        {
                            prevTestSelection = testSelection.mrn;
                            this.exportDataCriteria.tests.push({mrn: testSelection.mrn, visitIds: [testSelection.eyes[0].visitId]});
                            index++
                        }
                        else
                        {
                            this.exportDataCriteria.tests[index - 1].visitIds.push(testSelection.eyes[0].visitId)
                        }
                    }
                    await this.EXPORT_RESULT_DATA(this.exportDataCriteria);
                    this.exportAlert = true;
                    setTimeout(() =>
                    {
                        this.exportAlert = false;
                    }, 1500);
                    this.RESET_MRN_SELECTIONS()
                }
                catch (error)
                {
                    this.waitingOnServer = false;
                    this.SHOW_SNACKBAR_ERROR(error.message);
                    return
                }
                this.close();
                await this.loadItems()
            },
            testSelected(selectMrn)
            {
                if (selectMrn && !this.mrnSelections.some((item => item.mrn === this.mrnPredicate.mrn)))
                {
                    this.mrn.page = this.paginationComputed.page;
                    this.ADD_MRN_SELECTION_ONLY(this.mrn);
                    this.selected = this.mrnSelections
                }
                else if (!selectMrn && this.mrnSelections.some((item => item.mrn === this.mrnPredicate.mrn)))
                {
                    this.ADD_MRN_SELECTION_ONLY(this.mrn);
                    this.selected = this.mrnSelections
                }
            },
            async seeOrganizationTestsToggle()
            {
                this.testToken = null;
                await this.loadItems()
            },
            async notesItem(item)
            {
                if (await this.getTestsCommon(item.mrn))
                {
                    this.mrnNotesDialog = true
                }
            },
            mrnNotesDialogClose()
            {
                this.mrnNotesDialog = false
            },
            async trending(item)
            {
                if (await this.getTestsCommon(item.mrn))
                {
                    this.trendingDialog = true
                }
            },
            trendingDialogClose()
            {
                this.trendingDialog = false;
            },
            shouldCollapse(mrn)
            {
                this.expandOrHideRow = false;
                // Collapse the row if switching to a new row
                if (this.expanded.length === 1 && this.expanded[0].mrn !== mrn)
                {
                    this.expanded = []
                }
            },
            async getTestsCommon(mrn)
            {
                this.shouldCollapse(mrn)
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_TESTS({mrn: mrn});
                    return true
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                    return false
                }
                finally
                {
                    this.WAITING(false)
                }
            },
            formatDate(date)
            {
                if (!date)
                {
                    date = new Date()
                }
                return new Date(date).toLocaleDateString(navigator.languages[0], {timeZone: 'UTC'})
            },
            formatDateForCalendar(date)
            {
                if (!date)
                {
                    date = new Date()
                }
                return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
            },
            formatDateForTime(date)
            {
                if (!date)
                {
                    return '00:00:00'
                }
                return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
            }
        }
    }

</script>